import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, useMap, WMSTileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-side-by-side';
import LeafletControlGeocoder from './custom/MapSearch';
import { huaColorMaps, huaBundlesColorMaps } from '../../assets/data/huaColorMaps';
import { huaLabels, huaBundlesLabels } from '../../assets/data/huaLabels';
import { EcosystemServicesLayer, HuaLayer } from '../pages/Ecosystem';
import { RailwayMapControl } from './custom/MapFranceRailwayLayers';
import { HistoricalMapControl } from './custom/MapFranceHisotricalLayers';
import Cookies from 'js-cookie';
import GeoJsonControl from './custom/MapFranceCaseStudyLayers';
import { getCenterAndZoom } from './custom/MapFunctions';
import ExportControl from './custom/MapExportLayers';

type EcosystemMapProps = {
  leftLayer: HuaLayer | undefined;
  rightLayer: HuaLayer | undefined;
  indicator: string;
  leftScenario: string;
  rightScenario: string;
  type: string;
  data: EcosystemServicesLayer;
};

interface LegendBoxProps {
  title: string;
  colorMap: Record<number, [number, number, number, number]>;
  labels: string[];
  position?: 'left' | 'right';
}

const LegendBox: React.FC<LegendBoxProps> = ({ title, colorMap, labels, position = 'right' }) => {
  if (!colorMap || !labels) return null;

  const levels = Object.keys(colorMap)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div
      id='ecosystemMapLegendId'
      style={{
        padding: '12px',
        background: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
        position: 'absolute',
        top: '100px',
        [position]: '10px',
        zIndex: 1000,
        width: '250px',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Legend Title */}
      <strong
        style={{
          fontSize: '16px',
          display: 'block',
          marginBottom: '10px',
        }}
      >
        {title}
      </strong>

      {/* Legend Items */}
      <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
        {levels.map((level, idx) => {
          const [r, g, b, a] = colorMap[level];
          const rgba = `rgba(${r}, ${g}, ${b}, ${a})`;
          return (
            <div
              key={level}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  backgroundColor: rgba,
                  border: '1px solid #ccc',
                  marginRight: 8,
                }}
              />
              <span>{labels[idx]}</span>
            </div>
          );
        })}
      </ul>
    </div>
  );
};

const EcosystemMap: React.FC<EcosystemMapProps> = ({
  leftLayer,
  rightLayer,
  indicator,
  leftScenario,
  rightScenario,
  type,
  data,
}) => {
  const myLayer1Ref = useRef<L.TileLayer | null>(null);
  const myLayer2Ref = useRef<L.TileLayer | null>(null);
  const [franceCaseStudyLayerRef, setFranceCaseStudyLayerRef] = useState<any>(null);
  const [franceHistoricalControlRef, setFranceHistoricalControlRef] = useState<any>(null);
  const [franceRailwayLayerRef, setFranceRailwayLayerRef] = useState<any>(null);
  const [exportControlRef, setExportControlRef] = useState<any>(null);
  const caseStudy = Cookies.get('caseStudy');
  const getIndicatorLabel = (value: string): string => {
    const item = data.mapIndicators.find((dataItem) => dataItem.value === value);
    return item?.label ?? 'Unknown Label';
  };

  const SideBySideControl = () => {
    const map = useMap();

    useEffect(() => {
      if (!map) return;
      if (caseStudy) {
        const { center, zoom } = getCenterAndZoom(caseStudy);
        map.setView(center, zoom);
      }
      if (!exportControlRef) {
        const exportControl = new ExportControl({ position: 'topright' });
        exportControl.addTo(map);
        setExportControlRef(exportControl);
      }
      if (!franceHistoricalControlRef && caseStudy === 'pnr_narbonnaise') {
        const franceHistoricalControl = new HistoricalMapControl({ position: 'bottomleft' });
        franceHistoricalControl.addTo(map);
        setFranceHistoricalControlRef(franceHistoricalControl);
      }
      if (!franceRailwayLayerRef && caseStudy === 'pnr_narbonnaise') {
        const exportRailwayLayer = new RailwayMapControl({ position: 'bottomleft' });
        exportRailwayLayer.addTo(map);
        setFranceRailwayLayerRef(exportRailwayLayer);
      }
      if (!franceCaseStudyLayerRef) {
        const franceCaseStudyLayer = new GeoJsonControl({ position: 'bottomleft' });
        franceCaseStudyLayer.addTo(map);
        setFranceCaseStudyLayerRef(franceCaseStudyLayer);
      }
      if (!leftLayer && !rightLayer) return;

      // Clean up any existing layers
      if (myLayer1Ref.current) {
        map.removeLayer(myLayer1Ref.current);
        myLayer1Ref.current = null;
      }
      if (myLayer2Ref.current) {
        map.removeLayer(myLayer2Ref.current);
        myLayer2Ref.current = null;
      }

      let sideBySideControl: any;

      // Add left layer if it exists
      const myLayer1 = leftLayer
        ? L.tileLayer
          .wms('https://maps.plantnet.org/mapserver/hua', {
            layers: leftLayer.name,
            format: 'image/png',
            transparent: true,
          })
          .addTo(map)
        : null;

      if (myLayer1) myLayer1Ref.current = myLayer1;

      // Add right layer if it exists
      const myLayer2 = rightLayer
        ? L.tileLayer
          .wms('https://maps.plantnet.org/mapserver/hua', {
            layers: rightLayer.name,
            format: 'image/png',
            transparent: true,
          })
          .addTo(map)
        : null;

      if (myLayer2) myLayer2Ref.current = myLayer2;

      // Only add side-by-side control if both layers exist
      if (myLayer1 && myLayer2) {
        sideBySideControl = (L.control as any).sideBySide(myLayer1, myLayer2).addTo(map);

        requestAnimationFrame(() => {
          const divider = document.querySelector('.leaflet-sbs-divider') as HTMLElement;
          const mapContainer = document.getElementById('ecosystemMapId');
          if (divider && mapContainer) {
            divider.style.left = `${mapContainer.clientWidth / 2}px`;
          }
        });
      } else {
        // If only one layer exists, ensure no divider is left
        const divider = document.querySelector('.leaflet-sbs-divider');
        if (divider) divider.remove();
      }

      const timeout = setTimeout(() => map.invalidateSize(), 150);

      return () => {
        clearTimeout(timeout);
        if (myLayer1) map.removeLayer(myLayer1);
        if (myLayer2) map.removeLayer(myLayer2);
        if (sideBySideControl) sideBySideControl.remove();
      };
    }, [map, leftLayer, rightLayer]);

    return null;
  };

  return (
    <MapContainer
      center={[0, 0]}
      zoom={2}
      style={{ height: '800px', width: '100%', borderRadius: '10px', position: 'relative' }}
      id='ecosystemMapId'
    >
      <WMSTileLayer
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        attribution='&copy; OpenStreetMap contributors'
      />
      <LeafletControlGeocoder />
      <SideBySideControl />
      {/* <RecenterMap center={center} zoom={zoom} /> */}
      {type === 'services' && indicator && (leftScenario || rightScenario) && (
        <LegendBox
          title={getIndicatorLabel(indicator)}
          colorMap={huaColorMaps[indicator]}
          labels={huaLabels[indicator]}
        />
      )}
      {type === 'bundles' && indicator && (leftScenario || rightScenario) && (
        <>
          <LegendBox
            title={leftScenario.charAt(0).toUpperCase() + leftScenario.slice(1) + ` Scenario`}
            colorMap={huaBundlesColorMaps[leftScenario]}
            labels={huaBundlesLabels[leftScenario]}
            position='left'
          />
          <LegendBox
            title={rightScenario.charAt(0).toUpperCase() + rightScenario.slice(1) + ` Scenario`}
            colorMap={huaBundlesColorMaps[rightScenario]}
            labels={huaBundlesLabels[rightScenario]}
          />
        </>
      )}
    </MapContainer>
  );
};

export { EcosystemMap };
